/**
 * 六爻纳甲排盘引擎 —— 零依赖 ES 模块，浏览器 / Node 通用。
 *
 *   import { divine } from './src/index.js';
 *   const r = divine({ question: '这事成不成', category: '事业' });
 *   console.log(r.facts.join('\n'));
 *
 * 设计原则：
 *   1. 起卦的随机性、装卦的规则、旺衰的判定，全部是确定性可验证的代码；
 *   2. 引擎只输出事实，不输出结论；
 *   3. 想接 LLM，就把 r.facts 给它，让它组织语言，不要让它自己造卦。
 */

export * from './ganzhi.js';
export * from './hexagram.js';
export * from './cast.js';
export * from './analyze.js';
export * from './lunar.js';
export * from './meihua.js';
export * from './verdict.js';
export * from './verdict-thresholds.js';
export * from './verdict-weights.js';
export * from './date-candidates.js';
export * from './timing-rules.js';
export * from './timing.js';
export * from './report-view.js';

import { fourPillars } from './ganzhi.js';
import { cast } from './cast.js';
import { analyze } from './analyze.js';

/**
 * 起卦 + 排盘一步到位。
 *
 * @param {object} opts
 *   question    问题原文
 *   category    问事分类（见 USE_GOD_MAP），决定用神
 *   useRelative 直接指定用神六亲，优先于 category
 *   timeHorizon 'near' | 'far'；仅在问题明确远近时指定
 *   timeScope   'none' | 'near' | 'mid' | 'long'；控制报告展示的日期范围
 *   method      'coin'（默认）| 'yarrow' | 'manual'
 *   values      manual 时的六个爻值
 *   seed        指定则可复现
 *   at          起卦时刻 { year, month, day, hour, minute }，默认取当前时间
 *   tzOffset    时区，默认 8（传统命理一律用北京时间）
 */
export function divine(opts = {}) {
  const {
    question = '', category, useRelative, timeHorizon = null, timeScope = null,
    method, values, seed, at, ...rest
  } = opts;

  let moment = at;
  if (!moment) {
    const now = new Date();
    const tz = rest.tzOffset ?? 8;
    const local = new Date(now.getTime() + (tz * 60 + now.getTimezoneOffset()) * 60000);
    moment = {
      year: local.getFullYear(),
      month: local.getMonth() + 1,
      day: local.getDate(),
      hour: local.getHours(),
      minute: local.getMinutes()
    };
  }

  const pillars = fourPillars(moment, rest);
  const c = cast({ method, values, seed });
  const result = analyze({
    values: c.values, pillars, question, category, useRelative, timeHorizon, timeScope
  });

  return { ...result, at: moment, cast: c };
}

/**
 * 把排盘结果整理成给 LLM 的提示词。
 * 注意最后一段约束——它是防止模型自由发挥、把严谨排盘又变回胡诌的关键。
 */
export function toPrompt(result) {
  return [
    '以下是一次六爻纳甲排盘的结果，全部数据由确定性算法生成，不得改动。',
    '',
    `问题：${result.question || '（未填）'}`,
    `用神：${result.useGod ? result.useGod.用神 : '（未指定）'}`,
    '',
    result.facts.join('\n'),
    '',
    '请依据以上事实断卦。要求：',
    '1. 只能引用上面列出的爻位、六亲、干支、旺衰、动变，不得自行添加或修改任何一条；',
    '2. 断语要写明推理链条：用神是谁 → 旺衰如何 → 谁生谁克 → 所以结论；',
    '3. 明确说明这是易学推演，不是事实预测，不能替代医疗、法律、财务决策；',
    '4. 不确定处直接说不确定，不要为了圆满而编。'
  ].join('\n');
}
